import React from "react";
import { unstable_batchedUpdates as batch } from "react-dom";
import styled from "styled-components";
import { LooseAutocompleteInput } from "../__Internal__";
import { useAutocompleteFocusCapture } from "../hooks/useAutocompleteFocusCapture";
import { useDebounce } from "../hooks/useDebounce";
import { useEvent } from "../hooks/useEvent";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { useIsFocused } from "../hooks/useIsFocused";
import { PropError, PropLabel, PropNote, PropOption } from "../PropX";
import { dispatchChangeEvent } from "../utils/dispatchChangeEvent";
import { forkedRef } from "../utils/forkedRef";
import { ComboBoxCover } from "./ComboBoxCover";

type Props = {
  /**
   * The name attached to the input.
   */
  name?: string;

  /**
   * The id attached to the input.
   */
  id?: string;

  /**
   * The value of the input.
   */
  value: string;

  /**
   * A callback triggered when the input value changes.
   */
  onChange: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * A callback triggered when the input is selected.
   */
  onSelect?: (option: PropOption | null) => void;

  /**
   * A callback triggered when the input is blurred.
   * @default undefined
   */
  onBlur?: React.FocusEventHandler<HTMLDivElement>;

  /**
   * A callback triggered when the input is focused.
   * @default undefined
   */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;

  /**
   * The options to display in the dropdown menu.
   */
  options: PropOption[];

  /**
   * A message to display when there are no options to select from.
   * @default undefined
   */
  noOptionsMessage?: React.ReactNode;

  /**
   * Whether the input will autoComplete (only works on Safari).
   * @default "off"
   */
  autoComplete?: string;

  /**
   * Whether the input should spellcheck.
   * @default false
   */
  spellCheck?: boolean;

  /**
   * Placeholder text for the input.
   * @default ""
   */
  placeholder?: string;

  /**
   * A regular expression to validate string input against.
   * @default undefined
   */
  pattern?: string;

  /**
   * Whether the input is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the input is required.
   * @default false
   */
  required?: boolean;

  /**
   * Whether the input uses a monospace font.
   * @default false
   * @private
   */
  mono?: boolean;

  /**
   * Whether the input should be treated like a combo box.
   * @default false
   */
  comboBox?: boolean;

  /**
   * An element to display on the leftSide of the input.
   * @default undefined
   */
  leftSide?: React.ReactNode;

  /**
   * An element to display on the rightSide of the input.
   * @default undefined
   */
  rightSide?: React.ReactNode;

  /**
   * An optional header to display above the list of options.
   * @default null
   */
  listHeader?: React.ReactNode;

  /**
   * An optional footer to display below the list of options.
   * @default null
   */
  listFooter?: React.ReactNode;

  /**
   * The tabIndex for the input.
   * @default 0
   */
  tabIndex?: 0 | -1;

  /**
   * Testing utility tag.
   * @default undefined
   */
  "data-testid"?: string;

  /**
   * A label for the input.
   * @default undefined
   */
  label?: PropLabel;
  /**
   * A custom error to display for the input. Overrides standard errors created by `required` or `pattern`.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * The event for which to validate the input.
   * @default "blur"
   */
  validateOn?: "change" | "blur" | "manual";

  /**
   * The delay in milliseconds to wait before filtering the options. Only use if you are having performance issues.
   * @private
   * @default 0
   */
  UNSAFE_filterDelay?: number;

  /**
   * Whether to filter options in the dropdown menu. This is only meant to be disabled in very niche cases.
   * @private
   * @default true
   */
  UNSAFE_filterOptions?: boolean;

  /**
   * The number of items to render above and below the visible items in the list.
   * @private
   * @default 10
   */
  UNSAFE_overscanCount?: number;

  /**
   * Dynamically calculates the height of each item in the list. Necessary when items have variable heights.
   * WARNING: This is very slow and should only be used when absolutely necessary with a short list of items.
   * Should not be used in scenarios where the list of options is long or frequently changing.
   * @private
   * @default false
   */
  UNSAFE_precalculateItemHeights?: boolean;

  /**
   * If this is defined, selecting an option will no longer trigger a change event on the input or focus the input.
   * @default undefined
   * @deprecated Use `onSelect` instead.
   */
  UNSAFE_onSelect?: (option: PropOption) => void;
};

const InputContainer = styled.div`
  position: relative;
`;

const CoverContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

export const AutocompleteInput = React.forwardRef<HTMLInputElement, Props>((props, forwardedRef) => {
  const {
    id,
    name,
    value,
    options,
    label,
    leftSide,
    rightSide,
    noOptionsMessage,
    pattern,
    placeholder,
    spellCheck,
    onFocus,
    listHeader = null,
    listFooter = null,
    tabIndex = 0,
    mono = false,
    required = false,
    disabled = false,
    comboBox = false,
    validateOn = "blur",
    UNSAFE_filterDelay = 0,
    UNSAFE_filterOptions = true,
    UNSAFE_overscanCount = 10,
    UNSAFE_precalculateItemHeights = false,
  } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const inputIsFocused = useIsFocused(inputRef);

  const testId = props["data-testid"] || "autocomplete-input";

  const capture = useAutocompleteFocusCapture();

  const [selectedOption, setSelectedOption] = React.useState<PropOption | null>(null);

  const onSelect = useEvent((value: string, andFocusInput: boolean) => {
    batch(() => {
      const option = PropOption.fullMatchBySearch(props.options, value);

      /**
       * Option didn't change, don't do anything
       */
      if (option !== null && selectedOption?.value === option.value) {
        return;
      }

      if (option) {
        if (props.UNSAFE_onSelect) {
          props.UNSAFE_onSelect(option);
        } else {
          const input = inputRef.current;

          if (input) {
            dispatchChangeEvent(input, option.value);

            if (andFocusInput) {
              input.focus();
            }
          }
        }
      }

      props?.onSelect?.(option);
      setSelectedOption(option);
    });
  });

  /**
   * If the value ever changes while we the input is not focused, we want to attempt to select a value.
   */
  React.useEffect(() => {
    if (!capture.focused) {
      onSelect(value, false);
    }
  }, [capture.focused, value]);

  const onChange = useEvent((event: React.ChangeEvent<HTMLInputElement>) => {
    batch(() => {
      props.onChange(event);

      if (selectedOption !== null) {
        props?.onSelect?.(null);
      }

      setSelectedOption(null);
    });
  });

  const onClearComboBoxCover = useEvent(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    input.focus();
    dispatchChangeEvent(input, "");
    props?.onSelect?.(null);
    setSelectedOption(null);
  });

  const onHideComboBoxCover = useEvent(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    input.focus();
    dispatchChangeEvent(input, value);
    setSelectedOption(null);
  });

  /**
   * Only trigger the `onBlur` callback when we blur the broad `AutocompleteInput`.
   */
  const onBlurCapture = useEvent<React.FocusEventHandler<HTMLInputElement>>((ev) => {
    capture.onBlurCapture(ev, props.onBlur);
  });

  const [errors, propsForInput] = useHTMLInputElementValidationProjection(
    {
      error: props.error,
      disabled,
      value,
      pattern,
      required,
      onChange,
      onFocus,
    },
    {
      ref: inputRef,
      validateOn,
    }
  );

  const { invalid } = propsForInput;

  /**
   * When filtering search terms, we want to debounce the input to avoid excessive re-renders.
   */
  const filterSearch = useDebounce(UNSAFE_filterOptions ? value : "", value === "" ? 0 : UNSAFE_filterDelay);

  const bin = React.useMemo(() => {
    /**
     * If the user has selected a value from the dropdown, we want to hide the dropdown.
     */
    if (selectedOption?.value === value) {
      return { items: [], selected: [] };
    }

    return PropOption.bin(options, [], { filterSearch, filterSelectedFromItems: true });
  }, [options, selectedOption?.value, value, filterSearch]);

  const prefix = (() => {
    if (label) {
      return PropLabel.renderForBeforeInput(label, { required, invalid, htmlFor: name });
    }

    return null;
  })();

  const suffix = (() => {
    if (errors.some) {
      return PropError.renderGroupAfterInput(errors);
    }

    if (bin.items.length === 0 && inputIsFocused && !disabled && !!noOptionsMessage) {
      return PropNote.renderForAfterInput(noOptionsMessage);
    }

    return null;
  })();

  return (
    <div onFocusCapture={capture.onFocusCapture} onBlurCapture={onBlurCapture}>
      {prefix}
      <InputContainer>
        <LooseAutocompleteInput
          id={id}
          name={name}
          ref={forkedRef(inputRef, forwardedRef)}
          value={value}
          items={bin.items}
          tabIndex={comboBox && !!selectedOption ? -1 : tabIndex}
          leftSide={leftSide}
          rightSide={rightSide}
          pattern={pattern}
          placeholder={placeholder}
          onSelect={(value) => onSelect(value, !comboBox)}
          spellCheck={spellCheck}
          disabled={disabled}
          required={required}
          mono={mono}
          listHeader={listHeader}
          listFooter={listFooter}
          data-testid={testId}
          UNSAFE_showPopoverOnInvalid
          UNSAFE_forceHidePopover={!capture.focused}
          UNSAFE_popoverRepositionKey={errors.key}
          UNSAFE_overscanCount={UNSAFE_overscanCount}
          UNSAFE_precalculateItemHeights={UNSAFE_precalculateItemHeights}
          {...propsForInput}
        />
        {comboBox && !!selectedOption && (
          <CoverContainer>
            <ComboBoxCover
              name={name}
              disabled={disabled}
              option={selectedOption}
              onClear={onClearComboBoxCover}
              onClick={onHideComboBoxCover}
            />
          </CoverContainer>
        )}
      </InputContainer>
      {suffix}
    </div>
  );
});

AutocompleteInput.displayName = "TextInput.Autocomplete";
